import React, { useEffect, useState } from "react";
import axios from "axios";
import API_BASE_URL from "../../utils/config";

export default function PurchaseParsedPreview() {
  const [recordId, setRecordId] = useState(null);
  const [preview, setPreview] = useState([]);
  const [header, setHeader] = useState(null); // ✅ REQUIRED
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    supplier_name: "BIOPOLIS LIFESCIENCES PVT",
    invoice_number: "",
    invoice_date: ""
  });

  const token = localStorage.getItem("token");

  /* =========================
     Load NEXT pending record
  ========================= */
  const loadNext = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/purchase/next`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (!res.data) {
        setPreview([]);
        setHeader(null);
        setRecordId(null);
        setError("No pending purchase records 🎉");
        return;
      }

      const pj = res.data.parsed_json || {};

      setRecordId(res.data.id);
      setPreview(pj.items || []);

      setHeader({
        supplier_name: pj.supplier_name || "",
        bill_no: pj.bill_no || "",
        bill_date: pj.bill_date || "",
        due_date: pj.due_date || ""
      });

      // auto-fill form fields
      setForm({
        supplier_name: pj.supplier_name || "BIOPOLIS LIFESCIENCES PVT",
        invoice_number: pj.bill_no || "",
        invoice_date: pj.bill_date
          ? pj.bill_date.split("/").reverse().join("-") // DD/MM/YYYY → YYYY-MM-DD
          : ""
      });

    } catch (err) {
      setError(err.response?.data?.error || "Failed to load preview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNext();
  }, []);

  /* =========================
     Approve & auto-next
  ========================= */
  const handleApprove = async () => {
    if (!recordId) return;

    await axios.post(
      `${API_BASE_URL}/api/purchase/${recordId}/approve`,
      form,
      { headers: { Authorization: `Bearer ${token}` } }
    );

    loadNext();
  };

  /* =========================
     Reject & auto-next
  ========================= */
  const handleReject = async () => {
    if (!recordId) return;

    await axios.post(
      `${API_BASE_URL}/api/purchase/${recordId}/reject`,
      {},
      { headers: { Authorization: `Bearer ${token}` } }
    );

    loadNext();
  };

  return (
    <div className="p-6 max-w-5xl mx-auto bg-white shadow rounded">
      <h2 className="text-xl font-bold mb-4">Purchase Parsed Preview</h2>

      {loading && <p>Loading...</p>}
      {error && <p className="text-red-600">{error}</p>}

      {preview.length > 0 && (
        <>
          {/* ================= HEADER SUMMARY ================= */}
          {header && (
            <div className="mb-4 grid grid-cols-2 gap-4 bg-blue-50 p-3 rounded text-sm">
              <div><b>Supplier:</b> {header.supplier_name || "-"}</div>
              <div><b>Bill No:</b> {header.bill_no || "-"}</div>
              <div><b>Invoice Date:</b> {header.bill_date || "-"}</div>
              <div><b>Due Date:</b> {header.due_date || "-"}</div>
            </div>
          )}

          {/* ================= ITEMS TABLE ================= */}
          <table className="min-w-full text-sm border mt-4">
            <thead className="bg-gray-100">
              <tr>
                {Object.keys(preview[0]).map((key) => (
                  <th key={key} className="border px-2 py-1">
                    {key}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {preview.map((row, idx) => (
                <tr key={idx}>
                  {Object.entries(row).map(([key, val], i) => (
                    <td key={i} className="border px-2 py-1">
                      {val?.toString()}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>

          {/* ================= META FORM ================= */}
          <div className="mt-6 bg-gray-50 p-4 rounded">
            <div className="grid grid-cols-2 gap-4">
              <input
                value={form.supplier_name}
                onChange={e =>
                  setForm({ ...form, supplier_name: e.target.value })
                }
                className="border px-3 py-2 rounded"
                placeholder="Supplier Name"
              />

              <input
                value={form.invoice_number}
                onChange={e =>
                  setForm({ ...form, invoice_number: e.target.value })
                }
                className="border px-3 py-2 rounded"
                placeholder="Invoice Number"
              />

              <input
                type="date"
                value={form.invoice_date}
                onChange={e =>
                  setForm({ ...form, invoice_date: e.target.value })
                }
                className="border px-3 py-2 rounded"
              />

              <input
                type="date"
                value={
                  header?.due_date
                    ? header.due_date.split("/").reverse().join("-")
                    : ""
                }
                readOnly
                className="border px-3 py-2 rounded bg-gray-100 cursor-not-allowed"
                placeholder="Due Date"
              />
            </div>

            <div className="flex gap-4 mt-4">
              <button
                onClick={handleApprove}
                className="bg-green-600 text-white px-4 py-2 rounded"
              >
                Approve & Commit
              </button>

              <button
                onClick={handleReject}
                className="bg-red-600 text-white px-4 py-2 rounded"
              >
                Reject
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
